import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, fileUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { formatMoney } from "@/lib/currency";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1466706549280-a518a5abbad1?crop=entropy&cs=srgb&fm=jpg&w=1800&q=85";

export default function Storefront() {
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [heroImgLoaded, setHeroImgLoaded] = useState(false);
  // Storefront hero logo — prefer an admin-uploaded logo (settings.logo_path)
  // and fall back to the packaged Maison Pilet monogram bundled with the app.
  const heroLogo = settings?.logo_path ? fileUrl(settings.logo_path) : "/maison-pilet-logo.png";
  // Only fall back to HERO_FALLBACK when settings has loaded AND no user image is configured.
  // While settings is loading, heroImageSrc stays null so nothing renders (no default flash).
  const userHero = settings?.storefront_hero_path ? fileUrl(settings.storefront_hero_path) : null;
  const heroImageSrc = userHero || (settings ? HERO_FALLBACK : null);

  useEffect(() => {
    let cancelled = false;
    api.get("/storefront/inventory")
      .then((r) => { if (!cancelled) setItems(r.data.slice(0, 6)); })
      .catch((e) => {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("storefront inventory load failed", e);
        }
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="bg-ivory min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative" data-testid="hero-section">
        <div className="grid lg:grid-cols-12 gap-0 min-h-[78vh]">
          <div className="lg:col-span-5 flex flex-col justify-center px-8 lg:px-16 py-20 lg:py-0 bg-ivory">
            {heroLogo && (
              <img
                src={heroLogo}
                alt={settings?.legal_business_name || "Maison Pilet"}
                data-testid="hero-logo"
                className="h-[20rem] sm:h-[24rem] lg:h-[30rem] w-auto object-contain object-left mb-12 self-start animate-fade-up"
                style={{ animationDelay: "0s" }}
              />
            )}
            <span className="editorial-label mb-8 animate-fade-up" style={{ animationDelay: "0.05s" }}>maison pilet — vol. i</span>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl lowercase leading-[1.05] text-charcoal mb-8 animate-fade-up" style={{ animationDelay: "0.15s" }}>
              beautiful things deserve another story.
            </h1>
            <p className="text-base text-espresso leading-relaxed max-w-md tracking-wide mb-10 animate-fade-up" style={{ animationDelay: "0.3s" }}>
              An editorial resale boutique — pre-loved garments restored with intention,
              photographed in studio, and offered as quiet luxury for everyday living.
            </p>
            <div className="flex items-center gap-6 animate-fade-up" style={{ animationDelay: "0.45s" }}>
              <Link to="/shop" data-testid="hero-cta-shop" className="bg-brass hover:bg-espresso text-ivory px-8 py-4 text-sm tracking-[0.25em] uppercase transition-colors duration-300">
                enter the edit
              </Link>
              <span className="editorial-divider"></span>
              <span className="editorial-label">curated with intention</span>
            </div>
          </div>
          <div className="lg:col-span-7 relative overflow-hidden bg-linen group">
            {heroImageSrc && (
              <img
                src={heroImageSrc}
                alt="editorial atelier"
                onLoad={() => setHeroImgLoaded(true)}
                style={{ transition: "opacity 500ms ease-out, transform 2000ms ease-out" }}
                className={`absolute inset-0 w-full h-full object-contain object-center group-hover:scale-105 ${heroImgLoaded ? "opacity-100" : "opacity-0"}`}
              />
            )}
          </div>
        </div>
      </section>

      {/* Manifesto strip */}
      <section className="bg-linen border-y border-taupe/30">
        <div className="max-w-7xl mx-auto px-8 lg:px-16 py-16 grid md:grid-cols-3 gap-12">
          {["timeless over trendy.", "curated with intention.", "quiet luxury for everyday living."].map((t) => (
            <div key={t} className="text-center">
              <span className="editorial-divider mb-6 inline-block"></span>
              <p className="font-serif text-2xl lowercase text-charcoal">{t}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured edit */}
      <section className="max-w-7xl mx-auto px-8 lg:px-16 py-24" data-testid="featured-edit">
        <div className="flex items-end justify-between mb-16">
          <div>
            <span className="editorial-label">the latest edit</span>
            <h2 className="font-serif text-4xl lg:text-5xl lowercase text-charcoal mt-3">recently curated</h2>
          </div>
          <Link to="/shop" className="editorial-label hover:text-brass transition-colors">view all →</Link>
        </div>

        {items.length === 0 ? (
          <div className="border border-taupe/40 p-20 text-center">
            <p className="font-serif text-3xl lowercase text-charcoal mb-3">the atelier is preparing the next chapter.</p>
            <p className="editorial-label">beautiful things deserve another story.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12">
            {items.map((it, i) => (
              <Link to={`/product/${it.id}`} key={it.id} data-testid={`product-card-${i}`} className="group block">
                <div className="aspect-[3/4] bg-linen overflow-hidden mb-5">
                  {it.photos?.[0] ? (
                    <img src={fileUrl(it.photos[0])} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="font-serif text-2xl lowercase text-taupe">untitled</span>
                    </div>
                  )}
                </div>
                <p className="editorial-label">{it.brand || "—"}</p>
                <h3 className="font-serif text-2xl lowercase text-charcoal mt-1 group-hover:text-brass transition-colors">{it.title}</h3>
                <p className="text-sm text-espresso mt-2 tracking-wide">{formatMoney(it.sale_price)}</p>
              </Link>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
